import { useState } from 'react';

/**
 * A response, as something you can point at.
 *
 * The alternative — asking somebody to type `data.customer.CustomerID` from memory
 * of what their CRM returns — is the single most confusing thing the bot builder
 * asked of anybody. Here the answer is on screen and a field is remembered by
 * clicking it. Paths are the runner's own dotted form (`items.0.name`), so what is
 * picked here is exactly what `JsonPath` reads at runtime.
 */
export interface Pick { path: string; value: unknown; kind: 'value' | 'list' | 'object' }

export function JsonTree({ value, onPick, pickLists = false, pickValues = true, root = '', picked = {} }: {
  value: unknown;
  onPick?: (pick: Pick) => void;
  /** Whether arrays are pickable — for "where the list is in the response". */
  pickLists?: boolean;
  pickValues?: boolean;
  root?: string;
  /** Paths already remembered, and as what — shown beside the field so a click has a visible result. */
  picked?: Record<string, string>;
}) {
  if (value === null || value === undefined) return <p className="text-small text-(--color-muted)">Nothing came back.</p>;
  if (typeof value !== 'object') {
    return <Leaf path={root || '(the whole answer)'} value={value} picked={picked[root]} onPick={pickValues && onPick ? () => onPick({ path: root, value, kind: 'value' }) : undefined} />;
  }
  return (
    <ul className="font-mono text-small leading-relaxed">
      <Node value={value} path={root} depth={0} onPick={onPick} pickLists={pickLists} pickValues={pickValues} picked={picked} label={root ? undefined : 'the answer'} />
    </ul>
  );
}

const join = (base: string, key: string) => (base ? `${base}.${key}` : key);

function Node({ value, path, depth, label, onPick, pickLists, pickValues, picked }: {
  value: unknown; path: string; depth: number; label?: string;
  onPick?: (pick: Pick) => void; pickLists: boolean; pickValues: boolean; picked: Record<string, string>;
}) {
  const [open, setOpen] = useState(depth < 2);
  const isList = Array.isArray(value);
  const entries: [string, unknown][] = isList
    ? (value as unknown[]).slice(0, 20).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);
  const count = isList ? (value as unknown[]).length : entries.length;
  const name = label ?? path.split('.').pop() ?? '';
  const pickable = isList ? pickLists && onPick : false;

  return (
    <li>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => setOpen((v) => !v)} className="w-4 shrink-0 text-(--color-muted)" aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? '▾' : '▸'}
        </button>
        {pickable ? (
          <button type="button" onClick={() => onPick!({ path, value, kind: 'list' })} className="rounded px-1 text-(--color-brand) hover:bg-(--color-brand-wash)" title={`Use ${path || 'the answer'} as the list`}>
            {name}
          </button>
        ) : (
          <span className="text-(--color-ink)">{name}</span>
        )}
        <span className="text-(--color-faint)">{isList ? `list of ${count}` : `${count} field${count === 1 ? '' : 's'}`}</span>
        {picked[path] && <Badge name={picked[path]!} />}
      </div>
      {open && (
        <ul className="ml-4 border-l border-(--color-line-dim) pl-2">
          {entries.map(([key, child]) => {
            const childPath = join(path, key);
            return child !== null && typeof child === 'object'
              ? <Node key={key} value={child} path={childPath} depth={depth + 1} onPick={onPick} pickLists={pickLists} pickValues={pickValues} picked={picked} />
              : <Leaf key={key} path={childPath} value={child} picked={picked[childPath]} onPick={pickValues && onPick ? () => onPick({ path: childPath, value: child, kind: 'value' }) : undefined} />;
          })}
          {isList && count > 20 && <li className="text-(--color-faint)">… {count - 20} more</li>}
        </ul>
      )}
    </li>
  );
}

function Badge({ name }: { name: string }) {
  return (
    <span className="ml-1 shrink-0 rounded-full bg-(--color-brand-wash) px-1.5 text-micro font-sans font-medium text-(--color-brand)">
      ✓ {name}
    </span>
  );
}

function Leaf({ path, value, onPick, picked }: { path: string; value: unknown; onPick?: () => void; picked?: string }) {
  const key = path.split('.').pop() ?? path;
  const shown = value === null ? 'null' : typeof value === 'string' ? `"${value.length > 60 ? value.slice(0, 57) + '…' : value}"` : String(value);
  return (
    <li className="flex min-w-0 items-baseline gap-1.5">
      {onPick ? (
        <button type="button" onClick={onPick} className="shrink-0 rounded px-1 text-(--color-brand) hover:bg-(--color-brand-wash)" title={`Remember ${path}`}>
          {key}
        </button>
      ) : (
        <span className="shrink-0 text-(--color-ink)">{key}</span>
      )}
      <span className={`truncate ${typeof value === 'string' ? 'text-(--color-good)' : 'text-(--color-warn)'}`}>{shown}</span>
      {picked && <Badge name={picked} />}
    </li>
  );
}
